// Auth Context - Kullanıcı kimlik doğrulama state yönetimi
import { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const TOKEN_KEY = "kaan_token"; // App.js ile uyumlu

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY));
  const [isLoading, setIsLoading] = useState(true);
  const [anonymousLimit, setAnonymousLimit] = useState({ limit: 5, remaining: 5 });

  // Token değiştiğinde kullanıcı bilgilerini yükle
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      loadUser();
    } else {
      delete axios.defaults.headers.common["Authorization"];
      setUser(null);
      setIsLoading(false);
      checkAnonymousLimit();
    }
  }, [token]);

  const loadUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error("User load error:", error);
      logout();
    } finally {
      setIsLoading(false);
    }
  };

  const checkAnonymousLimit = async () => {
    try {
      const visitorId = localStorage.getItem("kaan-visitor-id") || 
        `visitor_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem("kaan-visitor-id", visitorId);
      
      const response = await axios.get(`${API}/chat/anonymous-limit?visitor_id=${visitorId}`);
      setAnonymousLimit(response.data);
    } catch (error) {
      console.error("Anonymous limit check error:", error);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const newToken = response.data.token;
    localStorage.setItem(TOKEN_KEY, newToken);
    setToken(newToken);
    return response.data;
  };

  const register = async (name, email, password) => {
    const response = await axios.post(`${API}/auth/register`, { name, email, password });
    const newToken = response.data.token;
    localStorage.setItem(TOKEN_KEY, newToken);
    setToken(newToken);
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setUser(null);
    checkAnonymousLimit();
  };

  const updateUser = (updates) => {
    setUser(prev => prev ? { ...prev, ...updates } : null);
  };

  const refreshCredits = async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error("Credits refresh error:", error);
    }
  };

  const value = {
    user,
    setUser,
    token,
    isLoading,
    anonymousLimit,
    login,
    register,
    logout,
    updateUser,
    refreshCredits,
    checkAnonymousLimit,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuthContext() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuthContext must be used within an AuthProvider");
  }
  return context;
}

export default AuthContext;
